import React, { useState } from "react";
import {
    Dialog,
    DialogFooter,
    DialogType,
    PrimaryButton,
    DefaultButton,
    TextField,
    Dropdown,
    Label,
    MessageBar,
    MessageBarType,
    Stack,
    Rating,
    RatingSize,
    Spinner,
    SpinnerSize,
} from "@fluentui/react";
import { DEFAULT_STYLE_OPTIONS, UploadImageFormData } from "../types/image";
import { getImageById } from "../services/image";

interface UploadImageDialogProps {
    isOpen: boolean;
    onDismiss: () => void;
    onUpload: (formData: UploadImageFormData) => Promise<void>;
}

const dialogContentProps = {
    type: DialogType.largeHeader,
    title: "Upload New Images",
    subText: "Fill in the image details and select files to upload. Style and Image ID are required and at least one file must be selected.",
};

const modalProps = {
    isBlocking: true,
    styles: { main: { maxWidth: 600 } },
};

export const UploadImageDialog: React.FC<UploadImageDialogProps> = ({
    isOpen,
    onDismiss,
    onUpload,
}) => {
    const [formData, setFormData] = useState<UploadImageFormData>({
        imageId: "",
        description: "",
        style: "",
        tags: "",
        rating: null,
        files: [],
    });
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isUploading, setIsUploading] = useState(false);
    const [imageIdError, setImageIdError] = useState<string | null>(null);

    const validateImageIdFormat = (imageId: string, style: string): string | null => {
        if (!imageId.trim()) {
            return "Image ID is required";
        }

        if (!style.trim()) {
            return "Style must be selected first";
        }

        // Find the selected style option to get the text
        const selectedStyleOption = DEFAULT_STYLE_OPTIONS.find((option) => option.key === style);
        if (!selectedStyleOption || selectedStyleOption.key === "") {
            return "Please select a valid style first";
        }

        // Expected format: {styleText}_0000 (where 0000 is a 4-digit number)
        const styleText = selectedStyleOption.text.replace(/\s+/g, "");
        const expectedPrefix = `${styleText}_`;

        if (!imageId.startsWith(expectedPrefix)) {
            return `Image ID must start with "${expectedPrefix}"`;
        }

        // Extract the number part after the prefix
        const numberPart = imageId.substring(expectedPrefix.length);

        // Check if it's exactly 4 digits
        const numberRegex = /^\d{4}$/;
        if (!numberRegex.test(numberPart)) {
            return `Image ID must end with exactly 4 digits (e.g., ${expectedPrefix}0001)`;
        }

        return null; // Valid format
    };

    const handleInputChange = (field: keyof UploadImageFormData, value: any) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));
        setError(null);
    };

    const handleImageIdChange = (value: string) => {
        setFormData((prev) => ({
            ...prev,
            imageId: value,
        }));
        setError(null);

        // Real-time validation for Image ID
        if (value && formData.style) {
            const validationError = validateImageIdFormat(value, formData.style);
            setImageIdError(validationError);
        } else {
            setImageIdError(null);
        }
    };

    const handleStyleChange = (styleKey: string) => {
        // Find the selected style option
        const selectedOption = DEFAULT_STYLE_OPTIONS.find((option) => option.key === styleKey);

        // Auto-populate ImageID based on style text (remove spaces and add underscore)
        let autoImageId = "";
        if (selectedOption && selectedOption.text && styleKey !== "") {
            autoImageId = `${selectedOption.text.replace(/\s+/g, "")}_0001`;
        }

        setFormData((prev) => ({
            ...prev,
            style: styleKey,
            imageId: autoImageId,
        }));
        setError(null);
        setImageIdError(null); // Clear validation error when style changes
    };

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(event.target.files || []);
        setFormData((prev) => ({
            ...prev,
            files,
        }));
        setError(null);
    };

    const validateForm = (): string | null => {
        if (!formData.style.trim()) {
            return "Style is required";
        }

        const imageIdError = validateImageIdFormat(formData.imageId, formData.style);
        if (imageIdError) {
            return imageIdError;
        }

        if (formData.files.length === 0) {
            return "At least one file must be selected";
        }
        return null;
    };

    const handleClose = () => {
        if (isUploading) {
            return; // 上传中不允许关闭
        }

        setFormData({
            imageId: "",
            description: "",
            style: "",
            tags: "",
            rating: null,
            files: [],
        });
        setError(null);
        setImageIdError(null);
        setIsLoading(false);
        setIsUploading(false);
        onDismiss();
    };

    const handleUpload = async () => {
        const validationError = validateForm();
        if (validationError) {
            setError(validationError);
            return;
        }

        try {
            setIsLoading(true);
            setIsUploading(true);
            setError(null);

            // 检查image ID是否已存在
            try {
                const existingImage = await getImageById(formData.imageId);
                if (existingImage) {
                    // 状态码200 - 图片已存在
                    setError(`Image ID "${formData.imageId}" already exists. Please use a different Image ID.`);
                    setIsUploading(false);
                    setIsLoading(false);
                    return;
                }
            } catch (checkError: any) {
                // 检查具体的HTTP状态码和错误消息
                const errorMessage = checkError.message || "";
                if (errorMessage.includes("not found") || errorMessage.includes("404")) {
                    // 404错误 - Image ID不存在，可以继续上传
                    console.log("Image ID does not exist, proceeding with upload...");
                } else {
                    // 其他错误 - 显示错误信息
                    console.error("Error checking image ID:", checkError);
                    setError(`Error checking image ID: ${errorMessage}`);
                    setIsUploading(false);
                    setIsLoading(false);
                    return;
                }
            }

            // 上传文件
            await onUpload(formData);

            // 上传完成，稍等一下再关闭
            setTimeout(() => {
                handleClose();
            }, 500);
        } catch (uploadError) {
            setError(uploadError instanceof Error ? uploadError.message : "Upload failed");
            setIsUploading(false);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Dialog
            hidden={!isOpen}
            onDismiss={handleClose}
            dialogContentProps={dialogContentProps}
            modalProps={modalProps}
        >
            <Stack tokens={{ childrenGap: 16 }}>
                {error && (
                    <MessageBar
                        messageBarType={MessageBarType.error}
                        onDismiss={() => setError(null)}
                    >
                        {error}
                    </MessageBar>
                )}

                {/* 上传状态 */}
                {isUploading && (
                    <Stack tokens={{ childrenGap: 8 }} horizontalAlign="center">
                        <Spinner
                            size={SpinnerSize.large}
                            label="Uploading..."
                            labelPosition="bottom"
                        />
                    </Stack>
                )}

                {/* Image ID - Required */}
                <TextField
                    label="Image ID"
                    required
                    value={formData.imageId}
                    onChange={(e, value) => handleImageIdChange(value || "")}
                    placeholder="Select style first, then format: StyleName_0001"
                    disabled={isLoading}
                    errorMessage={imageIdError || undefined}
                    description={formData.style ? `Format: ${DEFAULT_STYLE_OPTIONS.find((opt) => opt.key === formData.style)?.text.replace(/\s+/g, "")}_0001` : "Please select a style first"}
                />

                {/* Description */}
                <TextField
                    label="Description"
                    multiline
                    rows={3}
                    value={formData.description}
                    onChange={(e, value) => handleInputChange("description", value || "")}
                    placeholder="Enter image description..."
                    disabled={isLoading}
                />

                {/* Style Dropdown */}
                <Dropdown
                    label="Style"
                    required
                    options={DEFAULT_STYLE_OPTIONS.filter((option) => option.key !== "")}
                    selectedKey={formData.style}
                    onChange={(e, option) => handleStyleChange(String(option?.key || ""))}
                    placeholder="Select image style..."
                    disabled={isLoading}
                />

                {/* Tags */}
                <TextField
                    label="Tags"
                    value={formData.tags}
                    onChange={(e, value) => handleInputChange("tags", value || "")}
                    placeholder="Enter tags separated by commas..."
                    disabled={isLoading}
                />

                {/* Rating */}
                <Stack>
                    <Label>Rating</Label>
                    <Rating
                        max={5}
                        rating={formData.rating || 0}
                        size={RatingSize.Large}
                        onChange={(e, rating) => handleInputChange("rating", rating || null)}
                        disabled={isLoading}
                        allowZeroStars
                    />
                    <DefaultButton
                        text="Clear Rating"
                        onClick={() => handleInputChange("rating", null)}
                        disabled={isLoading || formData.rating === null}
                        styles={{ root: { marginTop: 8, width: "fit-content" } }}
                    />
                </Stack>

                {/* File Selection */}
                <Stack>
                    <Label required>Select Images</Label>
                    <input
                        type="file"
                        multiple
                        accept="image/*"
                        onChange={handleFileSelect}
                        disabled={isLoading}
                        style={{
                            padding: "8px",
                            border: "1px solid #ccc",
                            borderRadius: "4px",
                        }}
                    />
                    {formData.files.length > 0 && (
                        <div style={{ marginTop: 8, fontSize: "14px", color: "#106ebe" }}>
                            Selected
                            {" "}
                            {formData.files.length}
                            {" "}
                            file(s):
                            <ul style={{ margin: "4px 0", paddingLeft: "20px" }}>
                                {formData.files.map((file) => (
                                    <li key={file.name}>{file.name}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                </Stack>
            </Stack>

            <DialogFooter>
                <PrimaryButton
                    onClick={handleUpload}
                    text={isUploading ? "Uploading..." : "Upload Images"}
                    disabled={isLoading || isUploading}
                />
                <DefaultButton
                    onClick={handleClose}
                    text="Cancel"
                    disabled={isUploading}
                />
            </DialogFooter>
        </Dialog>
    );
};
